// src/lib/cloudSchemaRepair.ts
// ─────────────────────────────────────────────────────────────
// AUTO-REPARACIÓN DE ESQUEMA EN SUPABASE (columnas faltantes)
//
// Cuando la nube responde `Could not find the 'X' column of 'tabla' in the
// schema cache` (HTTP 400 / PGRST204) significa que el proyecto se creó con
// una versión anterior del esquema. En vez de fallar, aquí se:
//   1. detecta el nombre de la columna,
//   2. la crea con la función RPC `create_missing_column` (ya instalada),
//   3. reintenta la operación.
//
// Los avisos se muestran UNA vez por tabla+columna (consola limpia).
// ─────────────────────────────────────────────────────────────

import { supabase } from '@/lib/supabase';

const repaired = new Set<string>();

/** ¿El error dice que falta una columna? Devuelve su nombre. */
export const extractMissingColumn = (error: any): string | null => {
  const message = String(error?.message || '');
  const patterns = [
    /Could not find the '([^']+)' column/i,
    /column "?([a-zA-Z0-9_]+)"? of relation "?([a-zA-Z0-9_]+)"? does not exist/i,
    /column "?([a-zA-Z0-9_]+)"? does not exist/i,
  ];
  for (const re of patterns) {
    const match = message.match(re);
    if (match?.[1]) return match[1];
  }
  return null;
};

/**
 * ¿El error es porque la tabla NO tiene la restricción única que exige
 * `onConflict`? (Postgres 42P10) →
 * "there is no unique or exclusion constraint matching the ON CONFLICT specification"
 */
export const isOnConflictError = (error: any): boolean => {
  const code = String(error?.code || '');
  const message = String(error?.message || '').toLowerCase();
  return (
    code === '42P10' ||
    message.includes('no unique or exclusion constraint') ||
    (message.includes('on conflict') && message.includes('constraint'))
  );
};

/** Tipo SQL razonable para la columna faltante (según el valor de la app). */
export const inferColumnType = (value: unknown): string => {
  if (typeof value === 'boolean') return 'boolean';
  if (typeof value === 'number') return 'numeric';
  if (value !== null && typeof value === 'object') return 'jsonb';
  return 'text';
};

/** Crea la columna en la nube usando la RPC de auto-reparación. */
export const createMissingColumn = async (
  table: string,
  column: string,
  type = 'text',
): Promise<boolean> => {
  if (!supabase) return false;
  const key = `${table}.${column}`;
  try {
    const { error } = await (supabase as any).rpc('create_missing_column', {
      t_name: table,
      c_name: column,
      c_type: type,
    });
    if (error) {
      console.warn(`🛠️ [ESQUEMA] No se pudo crear ${key}:`, error.message);
      return false;
    }
    if (!repaired.has(key)) {
      repaired.add(key);
      console.info(`🛠️ [ESQUEMA] Columna creada automáticamente: public.${table}.${column} (${type})`);
    }
    return true;
  } catch (e: any) {
    console.warn(`🛠️ [ESQUEMA] Error creando ${key}:`, e?.message || e);
    return false;
  }
};

export interface SafeSaveResult {
  ok: boolean;
  error?: any;
  repairedColumns: string[];
  /** Cómo se guardó finalmente la fila. */
  method: 'UPSERT' | 'UPDATE' | 'INSERT' | 'NONE';
}

export interface SafeSaveOptions {
  /** Columna de conflicto para el upsert (ej: 'business_id'). */
  onConflict?: string;
  /** Columna lógica que identifica la fila (por defecto: onConflict). */
  keyColumn?: string;
  /** Si falta la PK en el INSERT, se rellena desde esta columna (ej: 'business_id'). */
  insertIdFrom?: string;
  maxAttempts?: number;
}

/**
 * GUARDADO SEGURO CON FALLBACK:
 *   1. Intenta el `upsert` (con auto-reparación de columnas faltantes).
 *   2. Si falla porque NO existe la restricción única del `onConflict`
 *      (42P10), hace el respaldo manual:
 *        · busca la fila por la columna clave;
 *        · si existe → UPDATE; si no → INSERT (rellenando la PK si hace falta).
 *
 * Así el estado de la tienda y de Naranja Plus se guarda SÍ O SÍ, aunque el
 * proyecto de Supabase tenga el esquema viejo sin la restricción UNIQUE.
 */
export const saveRowSafely = async (
  table: string,
  row: Record<string, any>,
  opts: SafeSaveOptions = {},
): Promise<SafeSaveResult> => {
  if (!supabase) return { ok: false, error: 'Supabase no configurado', repairedColumns: [], method: 'NONE' };

  const keyColumn = opts.keyColumn || opts.onConflict || 'business_id';
  const repairedColumns: string[] = [];
  const dropped = new Set<string>();
  const maxAttempts = Math.max(1, opts.maxAttempts ?? 6);
  let payload: Record<string, any> = { ...row };

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const { error } = await (supabase as any)
      .from(table)
      .upsert(payload, opts.onConflict ? { onConflict: opts.onConflict } : undefined);

    if (!error) return { ok: true, repairedColumns, method: 'UPSERT' };

    // ── Fallback 1: falta la restricción única del onConflict ──
    if (isOnConflictError(error)) {
      console.info(
        `🛠️ [NUBE] ${table} no tiene restricción UNIQUE en "${keyColumn}"; se usa el respaldo select/update/insert.`,
      );
      const keyValue = payload[keyColumn];
      try {
        const { data: existing, error: readError } = await (supabase as any)
          .from(table)
          .select(keyColumn)
          .eq(keyColumn, keyValue)
          .maybeSingle();
        if (readError && !extractMissingColumn(readError)) {
          console.warn(`🛠️ [NUBE] No se pudo leer ${table}:`, readError.message);
        }

        if (existing) {
          const { error: updError } = await (supabase as any)
            .from(table)
            .update(payload)
            .eq(keyColumn, keyValue);
          if (updError) return { ok: false, error: updError, repairedColumns, method: 'UPDATE' };
          return { ok: true, repairedColumns, method: 'UPDATE' };
        }

        // INSERT.
        // ⚠️ NO se envía `id` a ciegas: si la PK de la tabla es UUID, un id de
        // texto ("biz-…"/"conf-…") dispara
        // "invalid input syntax for type uuid". Se intenta primero SIN `id`
        // (la BD usa su default) y solo si exige la PK se rellena.
        const withoutId: Record<string, any> = { ...payload };
        delete withoutId['id'];

        let { error: insError } = await (supabase as any).from(table).insert(withoutId);

        if (insError && /null value in column "id"|violates not-null constraint/i.test(String(insError.message || ''))) {
          const withId: Record<string, any> = { ...withoutId };
          const derived = opts.insertIdFrom ? payload[opts.insertIdFrom] : undefined;
          if (derived !== undefined) {
            withId['id'] = String(derived);
            const retry = await (supabase as any).from(table).insert(withId);
            insError = retry.error;
          }
        }

        if (insError) {
          const missing = extractMissingColumn(insError);
          if (missing && !dropped.has(missing)) {
            await createMissingColumn(table, missing, inferColumnType(row[missing]));
            repairedColumns.push(missing);
            await new Promise((resolve) => setTimeout(resolve, 1200));
            continue;
          }
          return { ok: false, error: insError, repairedColumns, method: 'INSERT' };
        }
        return { ok: true, repairedColumns, method: 'INSERT' };
      } catch (fallbackError: any) {
        return { ok: false, error: fallbackError, repairedColumns, method: 'NONE' };
      }
    }

    // ── Fallback 2: falta una columna → se crea y se reintenta ──
    const missing = extractMissingColumn(error);
    if (!missing || dropped.has(missing)) {
      return { ok: false, error, repairedColumns, method: 'NONE' };
    }

    const created = await createMissingColumn(table, missing, inferColumnType(row[missing]));
    if (!created) {
      const { [missing]: _omit, ...rest } = payload;
      void _omit;
      payload = rest;
      dropped.add(missing);
      continue;
    }

    repairedColumns.push(missing);
    // Se espera un momento a que PostgREST refresque su schema cache antes de
    // reintentar (el RPC emite `pg_notify('pgrst','reload schema')`).
    await new Promise((resolve) => setTimeout(resolve, 1200));
  }

  return { ok: false, error: 'Demasiados reintentos de reparación de esquema', repairedColumns, method: 'NONE' };
};

/**
 * Compatibilidad: upsert con auto-reparación (incluye el fallback de
 * select/update/insert cuando falta la restricción UNIQUE).
 */
export const upsertWithRepair = async (
  table: string,
  row: Record<string, any>,
  opts: { onConflict?: string; maxAttempts?: number; insertIdFrom?: string } = {},
): Promise<{ ok: boolean; error?: any; repairedColumns: string[] }> => {
  const result = await saveRowSafely(table, row, opts);
  return { ok: result.ok, error: result.error, repairedColumns: result.repairedColumns };
};
